import Dropdown from "../../components/Dropdown";
import Button from "../../components/general/Button";

import { cn } from "@babybjorn/ui";
import type { TCountryCode } from "countries-list";
import { useState, type FC } from "react";
import { useAppState } from "../../context/app/context";
import { useSiteList } from "../../hooks/use-site-list";
import * as s from "./SiteSelectModal.module.scss";

export type SiteSelectionDropdownOption = {
	label: string;
	value: string;
	countryName: string;
	url: string;
	isSameSite: boolean;
	language: string;
	country: TCountryCode;
};

type SiteSelectModalProps = {
	title: string;
	preambleCountryExists: string;
	preambleCountryNotFound: string;
	closeButtonLabel: string;
	overlayOnClick?: () => Promise<void>;
	buttonOnClick: (selectedSite: SiteSelectionDropdownOption) => void;
	recommendedCountry?: TCountryCode;
};

const addUrlProtocol = (url: string) => {
	if (!url.startsWith("http")) {
		return `https://${url}`;
	}
	return url;
};

const SiteSelectModal: FC<SiteSelectModalProps> = ({
	title,
	closeButtonLabel,
	overlayOnClick,
	buttonOnClick,
	recommendedCountry,
	preambleCountryExists,
	preambleCountryNotFound,
}) => {
	const sitesList = useSiteList();
	const { allowedCountries } = useAppState();

	const options: Record<string, SiteSelectionDropdownOption> =
		Object.fromEntries(
			sitesList.map((site) => [
				`${site.countryIso}-${site.language}`,
				{
					label: `${site.countryName} | ${site.language.slice(0, 2).toUpperCase()}`,
					value: site.countryIso,
					icon: site.language.slice(0, 2).toUpperCase(),
					url: addUrlProtocol(`${site.domain}?country=${site.countryIso}`),
					isSameSite: site.isSameSite,
					language: site.language,
					country: site.countryIso,
					countryName: site.countryName,
				},
			]),
		);

	const recommendedKey = recommendedCountry
		? Object.keys(options).find((key) => key.startsWith(recommendedCountry))
		: undefined;

	const defaultValue =
		(recommendedKey && options[recommendedKey]) ||
		Object.values(options).find(
			(option) => option.country === allowedCountries[0],
		);

	if (defaultValue === undefined) {
		throw new Error("default country should not be undefined");
	}

	const [selectedSite, setSelectedSite] =
		useState<SiteSelectionDropdownOption>(defaultValue);

	return (
		<>
			<div
				className={cn(s.overlay)}
				onClick={overlayOnClick ? () => void overlayOnClick() : undefined}
				aria-hidden="true"
			/>
			<div className={cn(s.modal)}>
				<div className={cn(s.langPicker)}>
					<h3>{title}</h3>
					<p>
						{recommendedKey ? preambleCountryExists : preambleCountryNotFound}
					</p>
					<div className={cn(s.inputs)}>
						<div className={s.dropdown}>
							<Dropdown
								options={Object.values(options)}
								defaultValue={defaultValue}
								onChange={(value) => value && setSelectedSite(value)}
							/>
						</div>
						<Button
							onClick={() => buttonOnClick(selectedSite)}
							variant="primary"
							label={closeButtonLabel}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default SiteSelectModal;
